import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ChartModule } from 'primeng/chart';
import { DateTime } from 'luxon';
import { Subject, takeUntil } from 'rxjs';

import { AiSummaryStoreService } from '../../../services/stores/ai-summary-store.service';
import { PropertyStoreService } from '../../../services/stores/property-store.service';

import { AiSummary } from '../../../domain/Ai/ai-summary';
import { ModelSummary } from '../../../domain/Ai/model-summary';
import { AiDaySummary } from '../../../domain/Ai/ai-day-summary';

@Component({
   selector: 'app-graph-risk',
   standalone: true,
   imports: [CommonModule, ChartModule],
   templateUrl: './graph-risk.component.html',
   styleUrls: ['./graph-risk.component.scss']
})
export class GraphRiskComponent implements OnDestroy {
   chartData: any;
   chartOptions: any;
   loading = true;
   hasData = false;
   errorStatus: number | null = null;

   private destroy$ = new Subject<void>();

   constructor(
      private aiSummaryStore: AiSummaryStoreService,
      private propertyStore: PropertyStoreService,
      private router: Router
   ) {
      this.propertyStore.currentProperty
         .pipe(takeUntil(this.destroy$))
         .subscribe((property) => {
            if (!property?.id) {
               this.resetState();
               return;
            }

            const today = DateTime.now();

            this.loading = true;
            this.hasData = false;
            this.errorStatus = null;

            this.aiSummaryStore.summary$
               .pipe(takeUntil(this.destroy$))
               .subscribe((summaryResponse: AiSummary | null) => {
                  if (!summaryResponse?.summaryResponses?.length) {
                     this.loading = false;
                     this.hasData = false;
                     this.errorStatus = this.aiSummaryStore.getErrorStatus();
                     return;
                  }

                  const summaries = summaryResponse.summaryResponses;

                  this.hasData = summaries.some(
                     (s) => s.modelSummaries.filter((m) => !m.hasError).length > 0
                  );

                  if (!this.hasData) {
                     this.loading = false;
                     this.errorStatus = this.aiSummaryStore.getErrorStatus();
                     return;
                  }

                  // reset error if we got valid data
                  this.errorStatus = null;

                  const labels: string[] = [];
                  const dateCountMap: Record<
                     string,
                     { financialAmount: number; nonFinancialCount: number }
                  > = {};

                  for (let i = 29; i >= 0; i--) {
                     const date = today.minus({ days: i });
                     const isoDate = date.toISODate();
                     if (isoDate) {
                        dateCountMap[isoDate] = {
                           financialAmount: 0,
                           nonFinancialCount: 0
                        };
                        labels.push(date.toFormat('MMM d'));
                     }
                  }

                  summaries.forEach((summary: AiDaySummary) => {
                     summary.modelSummaries
                        .filter((model) => !model.hasError)
                        .forEach((model: ModelSummary) => {
                           const taskDate = model.taskDate;
                           if (!taskDate || !(taskDate in dateCountMap)) return;

                           if (model.isFinancial) {
                              dateCountMap[taskDate].financialAmount +=
                                 model.amount ?? 0;
                           } else {
                              dateCountMap[taskDate].nonFinancialCount +=
                                 model.summaryData?.length ?? 0;
                           }
                        });
                  });

                  const financialAmountData = Object.values(dateCountMap).map(
                     (day) => day.financialAmount
                  );
                  const nonFinancialCountData = Object.values(dateCountMap).map(
                     (day) => day.nonFinancialCount
                  );

                  this.chartData = {
                     labels,
                     datasets: [
                        {
                           label: 'Monetary Risk',
                           data: financialAmountData,
                           borderColor: '#d81818',
                           tension: 0.4,
                           fill: false,
                           pointRadius: 2,
                           pointHoverRadius: 10,
                           pointHitRadius: 10,
                           borderWidth: 2,
                           yAxisID: 'y'
                        },
                        {
                           label: 'Non-Monetary Tasks',
                           data: nonFinancialCountData,
                           borderColor: '#facc15',
                           tension: 0.4,
                           fill: false,
                           pointRadius: 2,
                           pointHoverRadius: 10,
                           pointHitRadius: 10,
                           borderWidth: 2,
                           yAxisID: 'y1'
                        }
                     ]
                  };

                  this.chartOptions = {
                     responsive: true,
                     onClick: (_event: MouseEvent, elements: any[]) => {
                        if (elements.length > 0) {
                           const elementIndex = elements[0].index;
                           const selectedDate = today.minus({
                              days: 29 - elementIndex
                           });
                           const selectedDateISO = selectedDate.toISODate();
                           const selectedMonth = selectedDate
                              .startOf('month')
                              .toISODate();

                           this.router.navigate(['/tasklist'], {
                              queryParams: {
                                 from: `/dashboard?selectedMonth=${selectedMonth}&type=1`,
                                 listType: 1,
                                 selecteddate: selectedDateISO
                              }
                           });
                        }
                     },
                     plugins: {
                        legend: {
                           position: 'bottom',
                           labels: {
                              color: '#1f2937',
                              usePointStyle: true,
                              boxWidth: 10
                           }
                        },
                        tooltip: {
                           mode: 'index',
                           intersect: false
                        },
                        datalabels: {
                           display: false
                        }
                     },
                     scales: {
                        x: {
                           ticks: { display: false },
                           grid: { display: false }
                        },
                        y: {
                           position: 'left',
                           title: {
                              display: true,
                              text: 'Monetary Risk',
                              color: '#d81818'
                           },
                           ticks: {
                              color: '#d81818',
                              callback: (value: number) =>
                                 value >= 1000 ? `$${value.toFixed(2)}` : `${value}`
                           },
                           grid: {
                              drawOnChartArea: false
                           }
                        },
                        y1: {
                           position: 'right',
                           title: {
                              display: true,
                              text: 'Non-Monetary Tasks',
                              color: '#facc15'
                           },
                           ticks: {
                              color: '#facc15',
                              stepSize: 1
                           },
                           grid: { color: '#e5e7eb' }
                        }
                     }
                  };

                  this.loading = false;
               });
         });
   }

   private resetState(): void {
      this.loading = false;
      this.hasData = false;
      this.errorStatus = null;
      this.chartData = null;
      this.chartOptions = null;
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }
}
